import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const CATEGORY_LABELS = {
  main_dish: 'Main Dish',
  side: 'Side',
  power_side: 'Power Side',
  starter: 'Starter',
  drink_dessert_treat: 'Drink / Dessert / Treat',
  complementary: 'Complementary',
};

const FREE_CATEGORIES = new Set(['drink_dessert_treat', 'complementary']);
const PRICE_CAPS = {
  main_dish: '$9.99',
  side: '$5.00',
  starter: '$4.00',
  power_side: '$5.00',
  drink_dessert_treat: '$0.00',
  complementary: '$0.00',
};

function ComboCategoryDialog({
  openComboCategoryDialog,
  setOpenComboCategoryDialog,
  MealOffering,
  ComboMenuCategory,
  onUpdateMealOffering,
  updateLoading,
}) {
  const [values, setValues] = useState({});
  const [step, setStep] = useState('edit'); // 'edit' | 'confirm'

  useEffect(() => {
    if (MealOffering && typeof MealOffering === 'object') {
      const initial = {};
      Object.keys(MealOffering).forEach((key) => {
        initial[key] = Number(MealOffering[key]) || 0;
      });
      setValues(initial);
    }
  }, [MealOffering]);

  const categoryKeys = Object.keys(values);
  const paidCategoryKeys = categoryKeys.filter((k) => !FREE_CATEGORIES.has(k));
  const freeCategoryKeys = categoryKeys.filter((k) => FREE_CATEGORIES.has(k));
  const warnEntries = categoryKeys.filter((k) =>
    FREE_CATEGORIES.has(k) ? values[k] >= 1 : values[k] > 2
  );

  const handleChange = (key, raw) => {
    const cleaned = raw.replace(/^0+(?=\d)/, '');
    const n = parseInt(cleaned, 10);
    setValues((prev) => ({ ...prev, [key]: Number.isNaN(n) ? 0 : Math.max(0, n) }));
  };

  const increment = (key) => setValues((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
  const decrement = (key) =>
    setValues((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }));

  const label = (key) => CATEGORY_LABELS[key] || key.replace(/_/g, ' ');

  const buildInlineWarnings = () =>
    warnEntries.map((k) => {
      const cap = PRICE_CAPS[k] || 'TBD';
      const isFree = FREE_CATEGORIES.has(k);
      if (isFree) {
        return `${label(k)} is set to ${values[k]} — Compensation  is capped at $0.00 per combo order.`;
      }
      return `${label(k)} is set to ${values[k]} — customer can pick up to ${values[k]} items (Compensation for main dishes is capped at ${cap} per combo order).`;
    });

  const handleUpdate = () => {
    if (warnEntries.length > 0) {
      setStep('confirm');
      return;
    }
    if (onUpdateMealOffering) onUpdateMealOffering(values);
  };

  const handleConfirm = () => {
    if (onUpdateMealOffering) onUpdateMealOffering(values);
  };

  const handleClose = () => {
    setStep('edit');
    setOpenComboCategoryDialog(false);
  };

  return (
    <Dialog open={openComboCategoryDialog} maxWidth='md'>
      <div style={sx.body}>
        {step === 'edit' && (
          <>
            {/* Header */}
            <div style={sx.header}>
              <h6 style={sx.title}>Combo Menu Category Setup</h6>
              <button type='button' style={sx.closeBtn} onClick={handleClose} aria-label='Close'>
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            <p style={sx.freeDescription}>
              If the main dish quantity is set to 2 or more, the user can select up to 2 main
              dishes. Compensation for main dishes is capped at $9.99 per meal order. The same
              applies to sides and starters, with a maximum of $5,. Complimentary and
              dessert_drink_treat items are not compensated
            </p>

            {/* Paid category counters */}
            {paidCategoryKeys.length > 0 && (
              <div style={sx.topCountersGrid}>
                {paidCategoryKeys.map((key) => (
                  <div key={key} style={sx.counterCell}>
                    <span style={sx.counterCellLabel}>{label(key)}</span>
                    <div style={sx.counterActions}>
                      <button
                        type='button'
                        style={{
                          ...sx.circleBtn,
                          ...(values[key] <= 0 ? sx.circleBtnDisabled : {}),
                        }}
                        onClick={() => decrement(key)}
                        disabled={values[key] <= 0}
                        aria-label={`Decrease ${label(key)}`}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </button>
                      <input
                        type='text'
                        inputMode='numeric'
                        pattern='[0-9]*'
                        value={String(values[key])}
                        onChange={(e) => handleChange(key, e.target.value.replace(/\D/g, ''))}
                        style={sx.numInput}
                      />
                      <button
                        type='button'
                        style={sx.circleBtn}
                        onClick={() => increment(key)}
                        aria-label={`Increase ${label(key)}`}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Free category panel */}
            {freeCategoryKeys.length > 0 && (
              <div style={sx.freeSection}>
                <h3 style={sx.freeSectionTitle}>Free categories</h3>
                <p style={sx.freeDescription}>
                  Listing in the <span style={sx.textBold}>Free Category</span> helps attract more
                  customers, increase visibility, and drive more orders. It’s a great way to
                  <span style={sx.textBold}> showcase your items,</span> let customers try them, and
                  collect <span style={sx.textBold}> honest feedback </span> on your complimentary
                  offerings. Think of it as a{' '}
                  <span style={sx.textBold}>smart sampling strategy</span> that turns first-time
                  users into paying customers.
                </p>
                <div style={sx.freeCountersGrid}>
                  {freeCategoryKeys.map((key) => (
                    <div key={key} style={sx.counterCell}>
                      <span style={sx.counterCellLabel}>{label(key)}</span>
                      <div style={sx.counterActions}>
                        <button
                          type='button'
                          style={{
                            ...sx.circleBtn,
                            ...(values[key] <= 0 ? sx.circleBtnDisabled : {}),
                          }}
                          onClick={() => decrement(key)}
                          disabled={values[key] <= 0}
                          aria-label={`Decrease ${label(key)}`}
                        >
                          <RemoveIcon sx={{ fontSize: 16 }} />
                        </button>

                        <input
                          type='text'
                          inputMode='numeric'
                          pattern='[0-9]*'
                          value={String(values[key])}
                          onChange={(e) => handleChange(key, e.target.value.replace(/\D/g, ''))}
                          style={sx.numInput}
                        />

                        <button
                          type='button'
                          style={sx.circleBtn}
                          onClick={() => increment(key)}
                          aria-label={`Increase ${label(key)}`}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inline warning */}
            {warnEntries.length > 0 && (
              <div style={sx.warningBox}>
                <span style={sx.warningIcon}>⚠</span>
                <div style={sx.warningText}>
                  {buildInlineWarnings().map((msg, i) => (
                    <div key={i} style={{ marginBottom: i < warnEntries.length - 1 ? '4px' : 0 }}>
                      {msg}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={sx.footer}>
              <button type='button' style={sx.cancelBtn} onClick={handleClose}>
                Cancel
              </button>
              <button
                type='button'
                style={{ ...sx.updateBtn, ...(updateLoading ? sx.updateBtnDisabled : {}) }}
                onClick={handleUpdate}
                disabled={updateLoading}
              >
                {updateLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div style={sx.header}>
              <h6 style={sx.title}>Are you sure?</h6>
            </div>

            <div style={sx.confirmBody}>
              <WarningAmberIcon sx={{ fontSize: 40, color: '#E65100' }} />

              <p style={sx.confirmText}>
                This will activate categories instantly. You can start adding products, and
                customers will see them immediately. Proceed?
              </p>

              <div style={sx.confirmDetails}>
                {warnEntries.map((k) => {
                  const isFree = FREE_CATEGORIES.has(k);
                  return (
                    <div key={k} style={sx.confirmRow}>
                      <span style={sx.confirmCategory}>{label(k)}</span>
                      <span style={sx.confirmValue}>
                        {values[k]} items
                        {isFree ? ' (complimentary)' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={sx.footer}>
              <button type='button' style={sx.cancelBtn} onClick={() => setStep('edit')}>
                Go back
              </button>
              <button
                type='button'
                style={{ ...sx.confirmBtn, ...(updateLoading ? sx.updateBtnDisabled : {}) }}
                onClick={handleConfirm}
                disabled={updateLoading}
              >
                {updateLoading ? 'Updating...' : 'Confirm update'}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

const sx = {
  body: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 24px 20px',
    width: '640px',
    gap: '8px',
    backgroundColor: '#f9fafb',
    fontFamily: "'Inter Regular', sans-serif",
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  title: {
    fontFamily: "'Inter Bold', sans-serif",
    color: '#181436',
    fontSize: '18px',
    margin: 0,
    fontWeight: 700,
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
  },
  topWarningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    margin: '2px 0 12px',
    padding: '10px 12px',
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: '8px',
  },
  topWarningText: {
    color: '#5D4037',
    fontSize: '13px',
    margin: 0,
    lineHeight: '1.45',
  },
  topCountersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '10px',
    marginBottom: '6px',
  },
  counterCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px 8px',
    minHeight: '96px',
  },
  counterCellLabel: {
    fontSize: '14px',
    color: '#181436',
    // fontWeight: 600,
    fontFamily: 'Inter Regular',
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  counterActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  freeSection: {
    border: '1px solid #D0D5DD',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    padding: '12px',
    marginTop: '4px',
  },
  freeSectionTitle: {
    fontFamily: "'Inter Bold', sans-serif",
    color: '#181436',
    fontSize: '16px',
    margin: '0 0 10px 0',
  },
  freeCountersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px',
  },
  freeDescription: {
    color: '#29292eff',
    fontFamily: 'Inter Regular',
    letterSpacing: '0.5px',
    fontSize: '12.5px',
    lineHeight: '1.5',
    margin: '0 0 10px 0',
  },
  textBold: {
    fontFamily: 'Inter Bold',
    color: '#181436',
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '10px 14px',
  },
  label: {
    fontSize: '14px',
    color: '#181436',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  circleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '1px solid #D0D5DD',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    color: '#181436',
  },
  circleBtnDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
  numInput: {
    width: '48px',
    textAlign: 'center',
    border: '1px solid #D0D5DD',
    borderRadius: '6px',
    padding: '4px 0',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#181436',
    outline: 'none',
    MozAppearance: 'textfield',
    WebkitAppearance: 'none',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: '8px',
  },
  warningIcon: {
    fontSize: '16px',
    lineHeight: '1.4',
    flexShrink: 0,
  },
  warningText: {
    fontSize: '12.5px',
    color: '#5D4037',
    lineHeight: '1.5',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '18px',
  },
  cancelBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '6px 8px',
    color: 'gray',
    fontFamily: 'inherit',
    borderRadius: '4px',
  },
  updateBtn: {
    padding: '8px 22px',
    backgroundColor: '#181436',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
    fontWeight: 500,
  },
  updateBtnDisabled: {
    backgroundColor: '#e0e0e0',
    color: '#757575',
    cursor: 'not-allowed',
  },
  // Confirm step
  confirmBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 0',
  },
  confirmText: {
    fontSize: '14px',
    color: '#181436',
    lineHeight: '1.6',
    textAlign: 'center',
    margin: 0,
  },
  confirmDetails: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '4px',
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: '6px',
  },
  confirmCategory: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#E65100',
  },
  confirmValue: {
    fontSize: '13px',
    color: '#5D4037',
  },
  confirmBtn: {
    padding: '8px 22px',
    backgroundColor: '#E65100',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
    fontWeight: 500,
  },
};

export default ComboCategoryDialog;
